import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Briefcase, Building2, Users, Wallet, Banknote, BarChart3, CheckSquare, Settings as SettingsIcon, LogOut, Upload, Archive, Megaphone, PenLine, ShieldAlert, Inbox, FileSignature } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const nav = [
  { to: "/app", end: true, icon: LayoutDashboard, label: "Dashboard", tid: "nav-dashboard-link" },
  { to: "/app/opdrachtgevers", icon: Briefcase, label: "Opdrachtgevers", tid: "nav-clients-link" },
  { to: "/app/panden", icon: Building2, label: "Panden & Adressen", tid: "nav-properties-link" },
  { to: "/app/huurders", icon: Users, label: "Debiteuren", tid: "nav-tenants-link" },
  { to: "/app/contracten", icon: FileSignature, label: "Contracten", tid: "nav-contracts-link", modKey: "contracts" },
  { to: "/app/postvak", icon: Inbox, label: "Postvak", tid: "nav-inbox-link", modKey: "postvak", badge: "inbox" },
  { to: "/app/openstaand", icon: Wallet, label: "Openstaand", tid: "nav-outstanding-link", modKey: "outstanding" },
  { to: "/app/bulk-aanmaningen", icon: Megaphone, label: "Bulk-aanmaningen", tid: "nav-bulk-dunning-link", modKey: "bulk-dunning" },
  { to: "/app/brievengenerator", icon: PenLine, label: "Brievengenerator", tid: "nav-letter-generator-link", modKey: "letter-generator" },
  { to: "/app/klachten", icon: ShieldAlert, label: "Klachten", tid: "nav-complaints-link", modKey: "complaints" },
  { to: "/app/uitbetalingen", icon: Banknote, label: "Uitbetalingen", tid: "nav-payouts-link", modKey: "payouts" },
  { to: "/app/rapportages", icon: BarChart3, label: "Rapportages", tid: "nav-reports-link", adminOnly: true, modKey: "reports" },
  { to: "/app/taken", icon: CheckSquare, label: "Taken", tid: "nav-tasks-link", modKey: "tasks" },
  { to: "/app/importeren", icon: Upload, label: "Importeren", tid: "nav-import-link", adminOnly: true, modKey: "import" },
  { to: "/app/archief", icon: Archive, label: "Archief", tid: "nav-archive-link", modKey: "archive" },
  { to: "/app/instellingen", icon: SettingsIcon, label: "Instellingen", tid: "nav-settings-link", adminOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [hidden, setHidden] = useState([]);
  const [inboxCount, setInboxCount] = useState(0);
  const location = useLocation();
  useEffect(() => {
    const refetch = () => api.get("/catalog").then((r) => setHidden(r.data.hidden_modules || [])).catch(() => {});
    refetch();
    window.addEventListener("modules-changed", refetch);
    return () => window.removeEventListener("modules-changed", refetch);
  }, [location.pathname]);
  useEffect(() => {
    const tick = () => api.get("/inbound/count").then((r) => setInboxCount(r.data.pending || 0)).catch(() => {});
    tick();
    const iv = setInterval(tick, 30000);
    return () => clearInterval(iv);
  }, []);
  const visibleNav = nav.filter((n) => (!n.adminOnly || isAdmin) && !(n.modKey && hidden.includes(n.modKey)));
  const initials = (user?.name || user?.email || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col bg-[#0F1E3D] text-slate-300 fixed inset-y-0 left-0 z-30 border-r border-[#C9A227]/20">
        <div className="p-6 flex items-center gap-3 border-b border-[#C9A227]/30">
          <div className="h-10 w-10 rounded-xl bg-white flex items-center justify-center shrink-0 p-1 ring-1 ring-[#C9A227]/60">
            <img src="/logo.png" alt="Praktisch-Debiteurenbeheer" className="h-full w-full object-contain" />
          </div>
          <div>
            <div className="font-heading font-bold text-white leading-tight text-sm">Praktisch</div>
            <div className="text-[11px] tracking-[0.18em] uppercase text-[#C9A227]">Debiteurenbeheer</div>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {visibleNav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.tid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors border-l-2 ${
                  isActive ? "bg-white/10 text-white border-[#C9A227]" : "border-transparent text-slate-300 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (<>
              <n.icon className={`h-5 w-5 ${isActive ? "text-[#C9A227]" : ""}`} />
              {n.label}
              {n.badge === "inbox" && inboxCount > 0 && (
                <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 rounded-full bg-[#C9A227] text-[#0F1E3D] text-[11px] font-bold px-1.5" data-testid="nav-inbox-count">{inboxCount}</span>
              )}
              </>)}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#C9A227]/20 text-xs text-slate-500">© Praktisch-Debiteurenbeheer</div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 md:ml-64 flex flex-col min-h-screen">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-8 sticky top-0 z-20 shadow-[inset_0_-2px_0_0_rgba(201,162,39,0.35)]">
          <div className="md:hidden font-heading font-bold text-[#0F1E3D]">Praktisch-Debiteurenbeheer</div>
          <div className="hidden md:block text-sm text-slate-500">Beheeromgeving</div>
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="profile-menu-trigger" className="flex items-center gap-3 outline-none">
              <div className="text-right hidden sm:block">
                <div className="text-sm font-semibold text-slate-800 leading-tight">{user?.name || "Beheerder"}</div>
                <div className="text-xs text-slate-400">{user?.email}</div>
              </div>
              <Avatar className="h-9 w-9 border border-slate-200">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-[#0F1E3D] text-[#C9A227] text-xs">{initials}</AvatarFallback>
              </Avatar>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="logout-button" onClick={logout} className="text-red-600 focus:text-red-600">
                <LogOut className="h-4 w-4 mr-2" /> Uitloggen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-[#0F1E3D] border-t border-[#C9A227]/40 flex justify-around py-2">
          {nav.slice(0, 4).map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              className={({ isActive }) => `flex flex-col items-center gap-0.5 px-2 py-1 text-[10px] ${isActive ? "text-[#C9A227]" : "text-slate-400"}`}>
              <n.icon className="h-5 w-5" />
              {n.label.split(" ")[0]}
            </NavLink>
          ))}
        </nav>

        <main className="flex-1 min-w-0 px-4 sm:px-8 py-6 max-w-7xl w-full mx-auto pb-24 md:pb-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
